import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RestaurantInfoService } from '../../../services/restaurant-info/restaurant-info.service';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { RestaurantResponse, RestaurantVerificationEnum } from "libs/shared-models/src/lib/restaurant/restaurant-response";
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RestaurantOrdersService } from '../../../services/restaurant-orders/restaurant-orders.service';
import { DeliveryProvider, OrderResponse, OrderStatus } from 'libs/shared-models/src/lib/order/order-response';
import { ApiBaseService } from 'libs/shared-services/src/lib/api-base.service';
import { environment } from 'apps/restaurant/src/environments/environment';
import { ToasterService } from 'libs/shared-services/src/lib/toaster.service';
import { LocaleService } from 'libs/shared-services/src/lib/locale.service';
import { DeliveryOption } from 'libs/shared-models/src/lib/delivery-option';
import { LOADING_STYLE } from 'libs/shared-ui/src/lib/fs-loading-spinner/fs-loading-spinner.component';
import { VerticalStatusItem, VerticalStatusItemState } from '../../general/vertical-status-progress/restaurant-vertical-status-progress.component';
import { ViewportScroller } from '@angular/common';

@UntilDestroy()
@Component({
  selector: 'web-foodis-section-orders',
  templateUrl: './section-orders.component.html',
  styleUrls: ['./section-orders.component.scss'],
})
export class SectionOrdersComponent implements OnInit {

  public isVerified$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public ordersList$: Observable<OrderResponse[]> = this.restaurantOrdersService.ordersList$();
  public currentSelectedOrder$: Observable<OrderResponse | null> = this.restaurantOrdersService.getCurrentSelectedOrder$();

  public isLoadingOrdersAPI$: Observable<boolean> = this.restaurantOrdersService.isLoadingOrdersAPI$();

  public restaurantData$: Observable<RestaurantResponse> = this.restaurantInfoService.getState$();

  // right side vertical steps
  public verticalStepsList$: BehaviorSubject<VerticalStatusItem[]> = new BehaviorSubject<VerticalStatusItem[]>([]);

  public DeliveryOption: typeof DeliveryOption = DeliveryOption;
  public PaymentOption: typeof PaymentOption = PaymentOption; 
  public OrderStatus: typeof OrderStatus = OrderStatus;
  public DeliveryProvider: typeof DeliveryProvider = DeliveryProvider;
  public LOADING_STYLE: typeof LOADING_STYLE = LOADING_STYLE;
  
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private restaurantInfoService: RestaurantInfoService,
    private restaurantOrdersService: RestaurantOrdersService,
    private apiService: ApiBaseService,
    private toasterService: ToasterService,
    private localeService: LocaleService,
    private viewportScroller: ViewportScroller
  ) {
    
  }

  public ngOnInit() {
    this.restaurantInfoService.getState$().pipe(untilDestroyed(this)).subscribe((res) => {
      this.isVerified$.next(res.verificationStatus === RestaurantVerificationEnum.VERIFIED);
    })   
    this.restaurantOrdersService.fetchOrdersListAPI();

    // listen to order being selected / getting data
    this.restaurantOrdersService.getCurrentSelectedOrder$().pipe(untilDestroyed(this)).subscribe((order) => {
      if (order) {
        this.setVerticalSteps(order);
      }
    })

    // listen for Route Order ID (eg: /orders/2222)
    this.route.paramMap.pipe(
      map((params) => params.get('id')),
      distinctUntilChanged(),
      switchMap((paramOrderId) => {
        if (!paramOrderId) {
          return EMPTY;
        }

        return this.restaurantOrdersService.isLoadingOrdersAPI$().pipe(
          filter((loading) => !loading),
          take(1),
          tap(() => this.restaurantOrdersService.fetchOrderByIdAPI(paramOrderId))
        );
      }),
      untilDestroyed(this)
    ).subscribe();

  }

  public onPressVerify() {
    this.router.navigateByUrl("/dashboard");
  }

  public onOpenOrder(order: OrderResponse) {
    this.router.navigateByUrl("/orders/" + order.id);
  }

  public pressStateChange(status: string, deliveryProvider?: DeliveryProvider) {

    let currentOrder = this.restaurantOrdersService.getCurrentSelectedOrder();
    
    if (currentOrder?.id) {

      let requestBody;

      if (status === "ACCEPTED_BY_RESTAURANT") {
        
        if (deliveryProvider) {
          requestBody = {
            status: status,
            estimatedTotalTimeInMinutes: 30,
            deliveryProvider: deliveryProvider
          }
        } else {
          requestBody = {
            status: status,
            estimatedTotalTimeInMinutes: 30
          }
        }
      } else {
        requestBody = {
          status: status
        }
      }

      let apiCall = this.apiService.patch(environment.API_GET_ORDERS_SET_STATUS.replace('{orderId}', currentOrder.id), requestBody);
      apiCall.pipe(
        map((res) => {
          //this.toasterService.showSuccess("", this.localeService.translate("accounts_invitation_removed_successfully"));

          this.toasterService.showSuccess("", "Order status updated successfully to " + status);

          // refetch orders:
          this.restaurantOrdersService.fetchOrdersListAPI();


          // TODO Remove this - let Backend do it after ACCEPTED_BY_RESTAURANT is changed
          // This is just to make the flow better
          if (status === "ACCEPTED_BY_RESTAURANT") {
            this.pressStateChange("PREPARING");
          }
        
          return true;
        }),
        catchError((err: any, caught: Observable<any>): Observable<any> => {
          this.toasterService.showError("Error", err?.error?.message);
          return of(false);
        })
      ).subscribe((res) => {
        console.log("==res: ", res);
      })
    }    
  } 



  /*
    Copy to clipboard
  */
    public onCopyClick() {
      const address = this.restaurantOrdersService.getCurrentSelectedOrder()?.userAddress;
      const coppiedAddress = address?.streetName + " " + address?.streetNumber + ", " + address?.district + " " + address?.postalCode + ", " + address?.city;
      // Copy the text inside the text field
      navigator.clipboard.writeText(coppiedAddress);
  
      this.toasterService.showInfo("",this.localeService.translate("orders_page_address_coppied"));
    }
  
    /*
      Open External Google maps
    */
    public onExternalMapsClick() {
      const address = this.restaurantOrdersService.getCurrentSelectedOrder()?.userAddress;
      const url = "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(address?.latitude + "," + address?.longitude); // + data.restaurantInfo.name
      window.open(url, '_blank');
    }


    // Vertical steps logic

    // TODO: create a generic component which takes care of this (combined with vertical-sttus-progress)

    private setVerticalSteps(order: OrderResponse) { 
    
      let steps = [];
  
      // delivery
      if (order?.deliveryOption === DeliveryOption.DELIVERY) {
        
        steps = this.getDeliverySteps();
  
        if (order?.status === OrderStatus.CREATED || order?.status === OrderStatus.PENDING_PAYMENT) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.ORDER_PAYMENT, VerticalStatusItemState.IN_PROGRESS);
        }
  
        if (order?.status === OrderStatus.PAID) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.RESTAURANT_CONFIRMATION, VerticalStatusItemState.IN_PROGRESS);
        }
  
        if (order?.status === OrderStatus.ACCEPTED_BY_RESTAURANT || 
            order?.status === OrderStatus.PREPARING
        ) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.FOOD_IN_PREPARATION, VerticalStatusItemState.IN_PROGRESS);
        }

        if (order?.status === OrderStatus.READY_FOR_PICKUP) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.WAITING_DRIVER, VerticalStatusItemState.IN_PROGRESS);
        }
  
        if (order?.status === OrderStatus.OUT_FOR_DELIVERY) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.IN_DELIVERY, VerticalStatusItemState.IN_PROGRESS);
        }
  
        if (order?.status === OrderStatus.DELIVERED ) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.DELIVERED, VerticalStatusItemState.COMPLETED);
        }  
      } else {
  
        // pick-up
        steps = this.getPickupSteps();
  
        // cash
        if (order?.paymentType === PaymentOption.CASH) {
          steps[1].required = false;
          steps[2].required = true;
  
          if (order?.status === OrderStatus.CREATED || order?.status === OrderStatus.PENDING_PAYMENT) {
            steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.RESTAURANT_CONFIRMATION, VerticalStatusItemState.IN_PROGRESS);
          }
        } else {
          // online
          steps[1].required = true;
          steps[2].required = false;
  
          if (order?.status === OrderStatus.CREATED || order?.status === OrderStatus.PENDING_PAYMENT) {
            steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.ORDER_PAYMENT, VerticalStatusItemState.IN_PROGRESS);
          }
        }
  
        if (order?.status === OrderStatus.PAID) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.RESTAURANT_CONFIRMATION, VerticalStatusItemState.IN_PROGRESS);
        }  
  
        if (order?.status === OrderStatus.ACCEPTED_BY_RESTAURANT || order?.status === OrderStatus.PREPARING) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.FOOD_IN_PREPARATION, VerticalStatusItemState.IN_PROGRESS);
        }  

  
        if (order?.status === OrderStatus.READY_FOR_PICKUP || order?.status === OrderStatus.OUT_FOR_DELIVERY) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.ORDER_PICKED_UP, VerticalStatusItemState.IN_PROGRESS);
        }
  
        if (order?.status === OrderStatus.DELIVERED) {
          steps = this.setCurrentStep(steps, VERTICAL_ITEM_NAME.ORDER_PICKED_UP, VerticalStatusItemState.COMPLETED);
        }
      }

      // set timestamps on each based on the order timestamp steps
      const timestamps = order?.statusTimestamps;

      if (timestamps[OrderStatus.CREATED]) {
        let step = steps.find((i) => i.id === VERTICAL_ITEM_NAME.ORDER_CREATED);
          if (step) {
            step.timestamp = timestamps[OrderStatus.CREATED];
          }
      }

      if (timestamps[OrderStatus.PAID]) {
        let step = steps.find((i) => i.id === VERTICAL_ITEM_NAME.ORDER_PAYMENT);
          if (step) {
            step.timestamp = timestamps[OrderStatus.PAID];
          }
      }

      if (timestamps[OrderStatus.ACCEPTED_BY_RESTAURANT]) {
        let step = steps.find((i) => i.id === VERTICAL_ITEM_NAME.RESTAURANT_CONFIRMATION);
          if (step) {
            step.timestamp = timestamps[OrderStatus.ACCEPTED_BY_RESTAURANT];
          }
      }

      if (timestamps[OrderStatus.READY_FOR_PICKUP]) {
        let step = steps.find((i) => i.id === VERTICAL_ITEM_NAME.FOOD_PREPARED_READY);
          if (step) {
            step.timestamp = timestamps[OrderStatus.READY_FOR_PICKUP];
          }
      }

      if (timestamps[OrderStatus.OUT_FOR_DELIVERY]) {
        let step = steps.find((i) => i.id === VERTICAL_ITEM_NAME.IN_DELIVERY);
          if (step) {
            step.timestamp = timestamps[OrderStatus.OUT_FOR_DELIVERY];
          }
      }

      if (timestamps[OrderStatus.DELIVERED]) {
        let step = steps.find((i) => i.id === VERTICAL_ITEM_NAME.DELIVERED);
          if (step) {
            step.timestamp = timestamps[OrderStatus.DELIVERED];
          }
      }
  
      this.verticalStepsList$.next(steps);
    }
  
    private getDeliverySteps(): VerticalStatusItem[] {
      return this.getSpecificSteps(INIT_VERTICAL_ITEMS_DELIVERY);
    }
  
    private getPickupSteps(): VerticalStatusItem[] {
      return this.getSpecificSteps(INIT_VERTICAL_ITEMS_PICKUP);
    }
  
    private getSpecificSteps(list: any[]) {
      let steps: VerticalStatusItem[] = [];
  
      list.forEach((item) => {
        const step = new VerticalStatusItem();
        step.id = item.id;
        step.order = item.order;
        step.required = item.required;
        step.translationKeyDefault = item.translationKeyDefault;
        step.translationKeyCompleted = item.translationKeyCompleted;
        step.status = item.status;
        steps.push(step);
      })
  
      return steps;
    }
  
    private setCurrentStep(list: VerticalStatusItem[], stateName: VERTICAL_ITEM_NAME, status: VerticalStatusItemState): VerticalStatusItem[] {
      const currentStep = list.find((i) => i.id === stateName);
      if (!currentStep) {
        return [];
      }
      
      list = list.map((i) => {
        // set to completed all steps until current status
        if (i.order < currentStep.order) {
          i.status = VerticalStatusItemState.COMPLETED;
        } else {
          // empty the future states
          i.status = VerticalStatusItemState.EMPTY;
        }
  
        if (i.id === currentStep.id) {
          currentStep.status = status;
        }
  
        return i;
      })
      return list;
    }
  }
  
  export enum VERTICAL_ITEM_NAME {
    ORDER_CREATED = "order_created",
    ORDER_PAYMENT = "order_payment",
    PAY_AT_PICKUP = "pay_at_pickup",
    RESTAURANT_CONFIRMATION = "restaurant_confirmation",
    FOOD_IN_PREPARATION = "food_preparation",
    FOOD_PREPARED_READY = "food_ready",
    WAITING_DRIVER = "wait_driver",
    IN_DELIVERY = "in_delivery",
    DELIVERED = "order_delivered",
    ORDER_PICKED_UP = "order_picked_up"
  }
  
  export const INIT_VERTICAL_ITEMS_DELIVERY = [
    {
      order: 1,
      required: true,
      id: VERTICAL_ITEM_NAME.ORDER_CREATED,
      translationKeyDefault: "order_vertical_status_delivery_step_1_default",
      translationKeyCompleted: "order_vertical_status_delivery_step_1_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 2,
      required: true,
      id: VERTICAL_ITEM_NAME.ORDER_PAYMENT,
      translationKeyDefault: "order_vertical_status_delivery_step_2_default",
      translationKeyCompleted: "order_vertical_status_delivery_step_2_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 3,
      required: true,
      id: VERTICAL_ITEM_NAME.RESTAURANT_CONFIRMATION,
      translationKeyDefault: "order_vertical_status_delivery_step_3_default",
      translationKeyCompleted: "order_vertical_status_delivery_step_3_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 4,
      required: true,
      id: VERTICAL_ITEM_NAME.FOOD_IN_PREPARATION,
      translationKeyDefault: "order_vertical_status_delivery_step_4_default",
      translationKeyCompleted: "order_vertical_status_delivery_step_4_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 5,
      required: true,
      id: VERTICAL_ITEM_NAME.FOOD_PREPARED_READY,
      translationKeyDefault: "order_vertical_status_delivery_step_5_default",
      translationKeyCompleted: "order_vertical_status_delivery_step_5_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 6,
      required: true,
      id: VERTICAL_ITEM_NAME.WAITING_DRIVER,
      translationKeyDefault: "order_vertical_status_delivery_step_6_default",
      translationKeyCompleted: "order_vertical_status_delivery_step_6_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 7,
      required: true,
      id: VERTICAL_ITEM_NAME.IN_DELIVERY,
      translationKeyDefault: "order_vertical_status_delivery_step_7_default",
      translationKeyCompleted: "order_vertical_status_delivery_step_7_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 8,
      required: true,
      id: VERTICAL_ITEM_NAME.DELIVERED,
      translationKeyDefault: "order_vertical_status_delivery_step_8_default",
      translationKeyCompleted: "order_vertical_status_delivery_step_8_completed",
      status: VerticalStatusItemState.EMPTY
    }
  ];
  
  
  export const INIT_VERTICAL_ITEMS_PICKUP = [
    {
      order: 1,
      required: true,
      id: VERTICAL_ITEM_NAME.ORDER_CREATED,
      translationKeyDefault: "order_vertical_status_pickup_step_1_default",
      translationKeyCompleted: "order_vertical_status_pickup_step_1_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 2,
      required: false,
      id: VERTICAL_ITEM_NAME.ORDER_PAYMENT,
      translationKeyDefault: "order_vertical_status_pickup_step_2_default",
      translationKeyCompleted: "order_vertical_status_pickup_step_2_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 3,
      required: false,
      id: VERTICAL_ITEM_NAME.PAY_AT_PICKUP,
      translationKeyDefault: "order_vertical_status_pickup_step_3_default",
      translationKeyCompleted: "order_vertical_status_pickup_step_3_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 4,
      required: true,
      id: VERTICAL_ITEM_NAME.RESTAURANT_CONFIRMATION,
      translationKeyDefault: "order_vertical_status_pickup_step_4_default",
      translationKeyCompleted: "order_vertical_status_pickup_step_4_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 5,
      required: true,
      id: VERTICAL_ITEM_NAME.FOOD_IN_PREPARATION,   
      translationKeyDefault: "order_vertical_status_pickup_step_5_default",
      translationKeyCompleted: "order_vertical_status_pickup_step_5_completed",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 6,
      required: true,
      id: VERTICAL_ITEM_NAME.FOOD_PREPARED_READY,
      translationKeyDefault: "order_vertical_status_pickup_step_6_default",
      translationKeyCompleted: "order_vertical_status_pickup_step_6_default",
      status: VerticalStatusItemState.EMPTY
    },
    {
      order: 7,
      required: true,
      id: VERTICAL_ITEM_NAME.ORDER_PICKED_UP,
      translationKeyDefault: "order_vertical_status_pickup_step_7_default",
      translationKeyCompleted: "order_vertical_status_pickup_step_7_completed",
      status: VerticalStatusItemState.EMPTY
    }
  ]


export enum PaymentOption {
  CASH = "CASH",
  ONLINE = "ONLINE"
}
